import {
    Stepper,
    Button,
    Typography,
    useTheme,
    Box,
    ClickAwayListener,
} from "@mui/material";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

import clsx from "clsx";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useStore } from "utils/useStore";
import { DataSourcesStore } from "./stores/DataSourcesStore";
import { DataHandlerStore } from "./stores/DataHandlerStore";
import { IntegrationStore } from "./stores/IntegrationsStore";
import DataSourceSetup from "./DataSourceSetup";
import SourceConfiguration from "./SourceConfiguration/SourceConfiguration";
import TargetMapping from "./TargetMapping/TargetMapping";
import ValueMapping from "./ValueMapping/ValueMapping";
import Advanced from "./Advanced/Advanced";
import DataSourcesTable from "./DataSourcesTable";
import DataSourceBreadcrumbs from "./Breadcrumbs";
import { v4 as uuidv4 } from "uuid";
import AmazonConnect from "./AmazonConnect";
import CustomFieldDialog from "./CustomFieldDialog";
import ManageExtendedMetadataDialog from "./ManageExtendedMetadataDialog";
import DiscardChangesDialog from "./DiscardChangesDialog";
import SaveConfigurationConfirmationDialog from "./SaveConfigurationConfirmationDialog";
import { AcxStep } from "components/UI/AcxStep";
import type { DataSourceType } from "./types";

const OrganizationDataSources = observer(() => {
    const theme = useTheme();
    const dataSourcesStore = useStore(DataSourcesStore);
    const dataHandlerStore = useStore(DataHandlerStore);
    const integrationStore = useStore(IntegrationStore);

    const {
        activeStep,
        setActiveStep,
        isFieldModalOpen,
        setIsFieldModalOpen,
        newFieldName,
        setNewFieldName,
        dataGridStore,
        selectedType,
        isIndexed,
        setIsIndexed,
        isManageFieldsModalOpen,
        setIsManageFieldsModalOpen,
        isDiscardModalOpen,
        setIsDiscardModalOpen,
        hasUnsavedChanges,
        discardChanges,
        setHasUnsavedChanges,
        canEdit,
    } = dataHandlerStore;

    const {
        loadData: loadDataSources,
        checkIfSourceConfigIsValid,
        checkIfSetupIsValid,
        customTargetFields,
        setCustomTargetFields,
        setup,
        selectedDataSource,
        checkIfKeyFieldsAreValid,
    } = dataSourcesStore;

    const {
        saveIntegration,
        selectedIntegration,
        loadData: loadIntegrations,
        isCheckKeysLoading,
    } = integrationStore;

    const [isSaving, setIsSaving] = useState(false);
    const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
    const [pendingSaveRequest, setPendingSaveRequest] =
        useState<DataSourceType | null>(null);
    const rawDiffSnapshot = useMemo(() => {
        if (!pendingSaveRequest || !selectedDataSource) {
            return null;
        }

        return {
            before: selectedDataSource,
            after: pendingSaveRequest,
        };
    }, [pendingSaveRequest, selectedDataSource]);

    const saveWithoutConfirmation = async (savePromise: Promise<void>) => {
        setIsSaving(true);
        try {
            setHasUnsavedChanges(false);
            await savePromise;
        } catch {
            setHasUnsavedChanges(true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveClick = () => {
        if (selectedType === "DataSource") {
            const request = dataSourcesStore.buildSaveRequest();
            if (!request) return;

            if (dataSourcesStore.shouldConfirmSave(request)) {
                setPendingSaveRequest(request);
                setIsConfirmSaveOpen(true);
            } else {
                saveWithoutConfirmation(dataSourcesStore.confirmAndSave(request));
            }
        } else if (selectedType === "Integration") {
            saveWithoutConfirmation(saveIntegration());
        }
    };
    const handleConfirmedSave = async () => {
        if (!pendingSaveRequest) return;
        setIsSaving(true);
        try {
            setHasUnsavedChanges(false);
            await dataSourcesStore.confirmAndSave(pendingSaveRequest);
            setIsConfirmSaveOpen(false);
            setPendingSaveRequest(null);
        } catch {
            setHasUnsavedChanges(true);
        } finally {
            setIsSaving(false);
        }
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const [pendingAction, setPendingAction] = useState<
        MouseEvent | TouchEvent | null
    >(null);

    const [manageExtendedData, setManageExtendedData] = useState(() =>
        customTargetFields.map((f) => ({
            ...f,
            isEditing: false,
            existingNameError: "", // Initialize with no error
        })),
    );

    useEffect(() => {
        setManageExtendedData(
            customTargetFields.map((f) => ({
                ...f,
                isEditing: false,
                existingNameError: "", // Reset error state when custom fields change
            })),
        );
    }, [customTargetFields]);

    const updateExtendedData = (
        id: string,
        updatedData: {
            label?: string;
            value?: string | number;
            isEditing?: boolean;
            isIndexed?: boolean;
            existingNameError?: string;
        },
    ) => {
        setManageExtendedData((prev) =>
            prev.map((data) =>
                data.id === id
                    ? {
                          ...data,
                          ...updatedData,
                      }
                    : data,
            ),
        );
    };

    const removeExtendedData = (id: string) => {
        setManageExtendedData((prev) => prev.filter((data) => data.id !== id));
    };

    const fileType = useMemo(() => {
        if (selectedDataSource && selectedType === "DataSource") {
            return setup.fileType;
        }

        if (selectedIntegration && selectedType === "Integration") {
            return selectedIntegration.type;
        }

        return "";
    }, [selectedDataSource, selectedIntegration, setup, selectedType]);

    const dataSourceValidation = useCallback(
        (index: number) => {
            // Key Fields are on step 3
            const handleKeyFields = () => {
                // If we're going past step 3, and the key fields are valid, go to selected step
                if (index > 3 && checkIfKeyFieldsAreValid()) {
                    return setActiveStep(index);
                }
                // If attempting to go to step 3, or the key fields are not valid, go to step 3
                if (index === 3 || !checkIfKeyFieldsAreValid()) {
                    setActiveStep(3);
                }
            };

            // Source Config Step - index 2
            const handleSourceConfig = () => {
                if (
                    // If we're going past step 2, and the source config is valid, go to step 3's handler
                    index > 2 &&
                    checkIfSourceConfigIsValid()
                ) {
                    return handleKeyFields();
                }
                if (
                    // If attempting to go to step 2 or the source config is not valid, go to step 2
                    index === 2 ||
                    !checkIfSourceConfigIsValid()
                ) {
                    setActiveStep(2);
                }
            };

            // Go back to the data grid
            if (index === 0) {
                setActiveStep(index);
            }
            if (
                // If we're going past 1, and the setup is valid, go to step 2's handler
                index > 1 &&
                checkIfSetupIsValid()
            ) {
                return handleSourceConfig();
            }
            if (
                // If attempting to go step 1 or the setup is not valid, go to step 1
                index === 1 ||
                !checkIfSetupIsValid()
            ) {
                setActiveStep(1);
            }
        },
        [
            checkIfSetupIsValid,
            checkIfSourceConfigIsValid,
            setActiveStep,
            checkIfKeyFieldsAreValid,
        ],
    );

    const amazonConnectValidation = useCallback(
        (index: number) => {
            // Validation is done on save
            setActiveStep(index);
        },
        [setActiveStep],
    );

    const stepsBySource = useMemo(() => {
        const dataSourceSteps = [
            {
                title: "Setup",
                stepLabel: "Setup",
                component: <DataSourceSetup store={dataSourcesStore} />,
            },
            {
                title: "Configuration",
                stepLabel: "Source Configuration",
                component: <SourceConfiguration store={dataSourcesStore} />,
            },
            {
                title: "Field Mapping",
                stepLabel: "Target Mapping",
                component: <TargetMapping store={dataSourcesStore} />,
            },
            {
                title: "Value Mapping",
                stepLabel: "Value Mapping",
                component: <ValueMapping store={dataSourcesStore} />,
            },
            {
                title: "Advanced Data Source Configuration",
                stepLabel: "Advanced",
                component: <Advanced store={dataSourcesStore} />,
            },
        ];

        return {
            JSON: { steps: dataSourceSteps, validation: dataSourceValidation },
            XML: { steps: dataSourceSteps, validation: dataSourceValidation },
            CSV: { steps: dataSourceSteps, validation: dataSourceValidation },
            FILENAME: {
                steps: dataSourceSteps,
                validation: dataSourceValidation,
            },
            AmazonConnect: {
                steps: [
                    {
                        title: "Set up Amazon Connect",
                        stepLabel: "Connection Details",
                        component: <AmazonConnect store={integrationStore} />,
                    },
                ],
                validation: amazonConnectValidation,
            },
        };
    }, [
        amazonConnectValidation,
        dataSourceValidation,
        dataSourcesStore,
        integrationStore,
    ]);

    const steps = useMemo(() => {
        const firstStep = {
            title: "Data Sources",
            stepLabel: "Data Sources",
            component: (
                <DataSourcesTable
                    dataHandlerStore={dataHandlerStore}
                    dataSourcesStore={dataSourcesStore}
                    integrationsStore={integrationStore}
                />
            ),
        };

        return fileType
            ? [firstStep, ...stepsBySource[fileType].steps]
            : [firstStep];
    }, [
        fileType,
        stepsBySource,
        dataSourcesStore,
        dataHandlerStore,
        integrationStore,
    ]);

    useEffect(() => {
        const loadData = async () => {
            dataGridStore.rows = [];
            dataGridStore.reset();
            setActiveStep(0);
            await Promise.all([loadDataSources(), loadIntegrations()]);
        };
        loadData();
    }, [loadIntegrations, loadDataSources, dataGridStore, setActiveStep]);

    const addCustomField = () => {
        //Check if field already exists
        if (customTargetFields.some((field) => field.value === newFieldName)) {
            return false;
        }
        setCustomTargetFields(
            [
                ...customTargetFields,
                {
                    label: newFieldName,
                    value: newFieldName,
                    isIndexed: isIndexed,
                    id: uuidv4(),
                },
            ].sort((a, b) => a.label.localeCompare(b.label)),
        );
        return true;
    };

    return (
        <ClickAwayListener
            mouseEvent="onMouseDown"
            touchEvent="onTouchStart"
            onClickAway={(e) => {
                const targetElement = e.target as HTMLElement;
                const isActionElement = /^(A|BUTTON)$/i.test(
                    targetElement.tagName,
                );

                if (hasUnsavedChanges && isActionElement) {
                    // Save the event to resume after confirming discard
                    setPendingAction(e);
                    setIsDiscardModalOpen(true);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
        >
            <Box
                ref={containerRef}
                role="presentation"
                sx={{
                    padding: 4,
                    maxWidth: `calc(100% - ${theme.spacing(4)})`,
                    width: "1200px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <DataSourceBreadcrumbs
                    store={dataHandlerStore}
                    fileType={fileType}
                    name={
                        selectedDataSource?.name ||
                        selectedIntegration?.type ||
                        ""
                    }
                />
                <Typography
                    sx={{
                        fontSize: "24px",
                        fontWeight: "600",
                        color: theme.palette.gray[700],
                    }}
                >
                    {steps[activeStep]?.title}
                </Typography>

                {/* 
                Stepper Navigation for Configuration
            */}
                {activeStep !== 0 && (
                    <Box
                        sx={{
                            justifyContent: "flex-start",
                            display: "flex",
                        }}
                    >
                        <Stepper nonLinear activeStep={activeStep}>
                            {steps.map(({ stepLabel }, index) => (
                                <AcxStep
                                    key={stepLabel}
                                    className={clsx({
                                        active: activeStep === index,
                                        inactive: activeStep !== index,
                                        first: index === 0,
                                        last: index === steps.length - 1,
                                    })}
                                >
                                    {/* Make sure step is valid before proceeding to subsequent steps */}
                                    <Button
                                        onClick={() =>
                                            stepsBySource[fileType].validation(
                                                index,
                                            )
                                        }
                                    >
                                        {stepLabel}
                                    </Button>
                                </AcxStep>
                            ))}
                        </Stepper>
                    </Box>
                )}

                {/* Render component for active step */}
                {steps[activeStep]?.component}

                {/* Progression and Cancel Buttons */}
                {activeStep !== 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "flex-end",
                            gap: 2,
                        }}
                    >
                        <AcxButton
                            color="white"
                            onClick={() => {
                                setActiveStep(activeStep - 1);
                            }}
                        >
                            {activeStep === 1 ? "Cancel" : "Back"}
                        </AcxButton>
                        {activeStep === steps.length - 1 ? (
                            <AcxButton
                                loading={isSaving}
                                color="primary"
                                disabled={isCheckKeysLoading || !canEdit}
                                onClick={handleSaveClick}
                            >
                                Save Configuration
                            </AcxButton>
                        ) : (
                            <AcxButton
                                color="primary"
                                onClick={() => {
                                    stepsBySource[fileType].validation(
                                        activeStep + 1,
                                    );
                                }}
                                endIcon={<ArrowRightAltIcon />}
                            >
                                Next
                            </AcxButton>
                        )}
                    </Box>
                )}
                <CustomFieldDialog
                    isOpen={isFieldModalOpen}
                    onClose={() => setIsFieldModalOpen(false)}
                    newFieldName={newFieldName}
                    setNewFieldName={setNewFieldName}
                    isIndexed={isIndexed}
                    setIsIndexed={setIsIndexed}
                    addCustomField={addCustomField}
                />
                <ManageExtendedMetadataDialog
                    isOpen={isManageFieldsModalOpen}
                    onClose={() => {
                        setIsManageFieldsModalOpen(false);
                        setManageExtendedData(
                            customTargetFields.map((f) => ({
                                ...f,
                                isEditing: false,
                                existingNameError: "",
                            })),
                        );
                    }}
                    manageExtendedData={manageExtendedData}
                    removeExtendedData={removeExtendedData}
                    updateExtendedData={updateExtendedData}
                    customTargetFields={customTargetFields}
                    setCustomTargetFields={setCustomTargetFields}
                />
                <DiscardChangesDialog
                    message="You have unsaved changes. If you navigate away now, your data source configuration will be lost."
                    isOpen={isDiscardModalOpen}
                    hasUnsavedChanges={hasUnsavedChanges}
                    pendingAction={pendingAction}
                    onDiscard={discardChanges}
                    setIsDiscardModalOpen={setIsDiscardModalOpen}
                    setPendingAction={setPendingAction}
                />
                {pendingSaveRequest && rawDiffSnapshot && (
                    <SaveConfigurationConfirmationDialog
                        isOpen={isConfirmSaveOpen}
                        onClose={() => setIsConfirmSaveOpen(false)}
                        onConfirm={handleConfirmedSave}
                        isLoading={isSaving}
                        before={rawDiffSnapshot.before}
                        after={rawDiffSnapshot.after}
                    />
                )}
            </Box>
        </ClickAwayListener>
    );
});

export default OrganizationDataSources;
